/**
 * The float left in the drawer overnight: which corte a new shift takes it over from, and what
 * the opening tells the books about it (see `caja.abierta`).
 *
 * Apart from the screen and the database so the rules can be tested, because a mistake here does
 * not show on screen: it books a float brought from general cash as a surplus, or a shortfall
 * overnight as nothing at all.
 */

import type { Centavos } from '@abastece/shared';
import type { SesionCaja } from '@abastece/core';

/** What the last corte at a drawer left in it: the float the next shift starts from. */
export interface Relevo {
  sesionId: string;
  queda: Centavos;
}

/**
 * The last corte on this device at `ubicacion`, if it left a float on the books. A device is a
 * drawer, as sessions are, so another device's corte says nothing about this one. A last corte
 * that kept no float — one from before it could — hands nothing on, even when an older one did:
 * the drawer went through that corte since.
 */
export function ultimoRelevo(
  sesiones: readonly SesionCaja[],
  dispositivoId: string,
  ubicacion: string,
): Relevo | null {
  let ultima: SesionCaja | undefined;
  for (const s of sesiones) {
    if (s.dispositivoId !== dispositivoId || s.ubicacion !== ubicacion || s.estado !== 'cerrada') continue;
    if (!ultima || (s.cerradaEn ?? '') > (ultima.cerradaEn ?? '')) ultima = s;
  }
  return ultima && ultima.queda != null ? { sesionId: ultima.id, queda: ultima.queda } : null;
}

/**
 * What an opening adds to `caja.abierta` to keep the float on the books. The count is held against
 * what the last corte left, plus `puesto`, what the opener says they put in from general cash: that
 * much comes out of general cash, and what the count finds over or short of it is the opening's.
 * Since 2026-10-14 every opening at «Empezar el día» is asked, a drawer with no float too (the
 * owner: «there should also be a step for putting in cash for change»). Not asked (`puesto`
 * undefined: the practice shop's seed, a till opened to try the app), a drawer with nothing to
 * hand on takes its whole count as the float, brought from general cash.
 */
export function fondoEnLibros(
  fondoInicial: Centavos,
  relevo: Relevo | null,
  puesto?: Centavos,
): { relevoDe?: string; esperado?: Centavos; deGeneral?: Centavos } {
  const quedo = relevo && relevo.queda > 0 ? relevo.queda : null;
  if (quedo !== null || puesto !== undefined) {
    return {
      ...(relevo ? { relevoDe: relevo.sesionId } : {}),
      esperado: (quedo ?? 0) as Centavos,
      ...(puesto !== undefined && puesto > 0 ? { deGeneral: puesto } : {}),
    };
  }
  return { ...(relevo ? { relevoDe: relevo.sesionId, esperado: 0 as Centavos } : {}), deGeneral: fondoInicial };
}
